import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import React, { useEffect } from 'react';
import { CircularProgress } from '@usepepper/ui/atoms/circular-progress';
import { createStyles } from '@/contexts/theme-context';
import { Dimensions, Platform, StyleProp, ViewStyle } from 'react-native';
import { LoadingDots } from '@usepepper/ui/atoms/loading-dots';

// as the user scrolls down, the threshould for the refresh to trigger is ~-112
export const DEFAULT_MAX_PROGRESS_OFFSET = 112;

export interface PullToRefreshLoaderProps {
  /**
   * Additional container style
   */
  containerStyle?: StyleProp<ViewStyle>;
  /**
   * Dynamic height based on scroll offset
   */
  height?: number;
  /**
   * Whether the component is enabled/visible
   */
  isEnabled: boolean;
  /**
   * Whether the user is currently dragging the scroll
   */
  isScrollDragging: boolean;
  /**
   * Progress value from 0 to 100
   */
  progress: number;
  /**
   * Whether to show loading dots instead of circle
   */
  showDots: boolean;
}

const TRANSITION_DURATION = 400;
const MIN_HEIGHT = 100; // Minimum height to keep dots visible during refresh

export const PullToRefreshLoader: React.FC<PullToRefreshLoaderProps> = ({
  progress,
  showDots,
  isEnabled,
  isScrollDragging,
  containerStyle,
  height,
}) => {
  const [styles] = useStyles();
  const opacitySharedValue = useSharedValue(1);
  const containerOpacitySharedValue = useSharedValue(0);
  const showDotsSharedValue = useSharedValue(0);
  const heightSharedValue = useSharedValue(0);

  useEffect(() => {
    showDotsSharedValue.value = withTiming(showDots ? 1 : 0, {
      duration: TRANSITION_DURATION,
      easing: Easing.out(Easing.ease),
    });
  }, [showDots, showDotsSharedValue]);

  useEffect(() => {
    const targetHeight = showDots
      ? Math.max(height ?? 0, MIN_HEIGHT)
      : (height ?? 0);

    if (isScrollDragging) {
      heightSharedValue.value = targetHeight;
    } else {
      heightSharedValue.value = withSpring(targetHeight, {
        damping: 20,
        stiffness: 90,
      });
    }
  }, [height, showDots, isScrollDragging, heightSharedValue]);

  useEffect(() => {
    if (isEnabled) {
      containerOpacitySharedValue.value = withSpring(1);
    } else {
      containerOpacitySharedValue.value = withSpring(0);
    }
  }, [isEnabled, containerOpacitySharedValue]);

  // Update opacity when showDots state changes (for circle/dots transition)
  useEffect(() => {
    if (showDots) {
      // Fade out circle, fade in dots
      opacitySharedValue.value = withTiming(0, {
        duration: TRANSITION_DURATION,
        easing: Easing.out(Easing.ease),
      });
    } else if (isEnabled) {
      opacitySharedValue.value = withTiming(1, {
        duration: TRANSITION_DURATION,
        easing: Easing.out(Easing.ease),
      });
    }
  }, [showDots, isEnabled, opacitySharedValue]);

  const containerOpacityStyle = useAnimatedStyle(() => ({
    opacity: containerOpacitySharedValue.value,
  }));

  const circleOpacityStyle = useAnimatedStyle(() => {
    const circleOpacity =
      (1 - showDotsSharedValue.value) * opacitySharedValue.value;
    return {
      opacity: circleOpacity,
    };
  });

  const dotsOpacityStyle = useAnimatedStyle(() => {
    return {
      opacity: showDotsSharedValue.value,
    };
  });

  // Animated height style to prevent jitter
  const animatedHeightStyle = useAnimatedStyle(() => {
    return {
      height: heightSharedValue.value,
    };
  });

  // Disable on android: https://usepepper.atlassian.net/browse/DSR-3478
  // Disable on web as well for consistency
  if (Platform.OS === 'android' || Platform.OS === 'web') {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        animatedHeightStyle,
        showDots && !isScrollDragging
          ? styles.containerRelative
          : styles.containerAbsolute,
        containerOpacityStyle,
        containerStyle,
      ]}
    >
      {/* Circular Progress - fades out when refreshing */}
      <Animated.View style={[styles.circleContainer, circleOpacityStyle]}>
        <CircularProgress
          progress={showDots ? 100 : progress}
          size={24}
          strokeWidth={3}
        />
      </Animated.View>

      {/* Loading Dots - fades in when refreshing */}
      <Animated.View style={[styles.dotsContainer, dotsOpacityStyle]}>
        <LoadingDots accessibilityLabel="Refreshing orders" size={32} />
      </Animated.View>
    </Animated.View>
  );
};

const useStyles = createStyles(() => ({
  circleContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: Dimensions.get('window').width,
  },
  containerAbsolute: {
    position: 'absolute',
  },
  containerRelative: {
    position: 'relative',
  },
  dotsContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
}));
